/**
 * Thin wrapper around the orders entity to fetch a customer's offers /
 * orders for the Sales Inbox sidebar.
 *
 * Uses the exact same data pipeline as the customer detail Offers/Orders
 * tabs and the orders list:
 *   url     → App.request('order:get-orders', params)        (commands.json/board)
 *   parse   → App.request('order:parse-list-data', rawRow)
 *   model   → App.request('order:model', data)               (app.ns.Order)
 *
 * The resulting collection contains real `app.ns.Order` models (Backbone
 * Relational, `findOrCreate`-deduplicated), so the sidebar stays in sync
 * with any Order instance the rest of the dashboard has already loaded
 * and gets the Order model's helper methods (`getProductName()` etc.) for
 * free.
 */
define('entities/customer_commands',[
  'app',
  'jquery',
  'underscore',
  'backbone',
  'entities/orders'
], function (App, $, _, Backbone) {
  'use strict';

  // Plain collection — the Order model class lives in entities/orders
  // (registered on Backbone.Relational as app.ns.Order). We just supply a
  // model factory that delegates to `order:model` so findOrCreate dedupes
  // correctly with any existing Order instance in the Relational store.
  var CustomerOrders = Backbone.Collection.extend({
    model: function (attrs) {
      return App.request('order:model', attrs);
    },
    comparator: function (m) {
      var d = m.get('createDate') || m.get('date');
      return -(d ? new Date(d).getTime() : 0);
    }
  });

  /**
   * Fetch commands.json/board filtered by endUserId + commandType and
   * return a Backbone collection of Order models — same normalisation as
   * the customer Offers / Orders tabs.
   */
  function fetchForCustomer(endUserId, commandType) {
    var defer = $.Deferred();
    if (!endUserId) {
      defer.resolve(new CustomerOrders());
      return defer.promise();
    }

    var params = {
      includeArchived: true,
      types: commandType,          // 'offer' or 'command'
      endUserId: endUserId,
      endUserIdFullMatch: 'true',
      pageSize: 50,
      first: 0,
      sord: 'DESC',
      sidx: 'date'
    };

    $.ajax({
      type: 'GET',
      url: App.request('order:get-orders', params),
      success: function (rows) {
        // Same flow as orders/list/view.js fetch():
        //   parseListData → order:model (findOrCreate)
        var models = _.map(rows || [], function (row) {
          return App.request('order:model', App.request('order:parse-list-data', row));
        });
        defer.resolve(new CustomerOrders(models));
      },
      // Caller may not have DASHBOARD_ORDERS / DASHBOARD_OFFERS — resolve
      // an empty collection so the sidebar shows "No offers" / "No orders"
      // rather than breaking the whole panel.
      error: function () { defer.resolve(new CustomerOrders()); }
    });

    return defer.promise();
  }

  App.reqres.setHandler('customerCommands:offers',     function (endUserId) { return fetchForCustomer(endUserId, 'offer'); });
  App.reqres.setHandler('customerCommands:orders',     function (endUserId) { return fetchForCustomer(endUserId, 'command'); });
  App.reqres.setHandler('customerCommands:collection', function (models)    { return new CustomerOrders(models || []); });

  return {
    Collection: CustomerOrders
  };
});

